import React, { useEffect } from 'react';
import { FileText, CreditCard, Clock, CheckCircle2, ShieldCheck, Download, Zap, MoveRight, Layers, FileSignature } from 'lucide-react';

export default function RadhaOnboarding() {

    useEffect(() => {
        document.title = "Onboarding | Leo's Pride via SocialSEO";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10 min-h-screen">
            {/* Background Gradient */}
            <div className="fixed inset-0 min-h-screen bg-[#080B11] -z-20 overflow-hidden">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-indigo-600/20 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute bottom-[-20%] right-[-10%] w-[60%] h-[60%] bg-emerald-600/15 blur-[160px] rounded-full mix-blend-screen pointer-events-none" />
                <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay pointer-events-none"></div>
            </div>

            {/* Navigation Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-[#07090E]/80 backdrop-blur-lg border-b border-white/5">
                <div className="flex items-center gap-3 w-full max-w-5xl mx-auto">
                    <span className="font-bold tracking-tight text-white text-xl">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/20 mx-2">/</span>
                    <span className="text-white/80 font-medium">Leo's Pride Onboarding</span>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 pt-10 space-y-16">
                
                {/* Intro Section */}
                <section>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-bold tracking-wide uppercase mb-6">
                        <CheckCircle2 className="w-4 h-4" /> Ready to Launch
                    </div>
                    
                    <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight">
                        Welcome to the Machine.
                    </h1>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8 space-y-4 text-white/80 text-lg leading-relaxed shadow-xl">
                        <p><strong>Hey Radha,</strong></p>
                        <p>Please find the finalized SLA attached below in the documents section. We strongly advise you to read through it properly to ensure complete alignment on deliverables. <strong>We will require the signed document before any production work begins.</strong></p>
                        <p>To give you complete clarity on how we’ll be executing the 12-video production pipeline, we want to establish expectations around timelines. Because establishing your custom visual identity, deep research, and "pinning" your AI characters requires heavy lifting upfront, the first batch of videos will take longer than subsequent ones.</p>
                        <p>We structure this in batches to establish a tight feedback loop and ensure the absolute highest quality. Instead of a rigid monthly cut-off, our agreement rolls over until all 12 videos are 100% completed and distributed.</p>
                    </div>
                </section>

                {/* Timeline Section */}
                <section>
                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <Clock className="text-indigo-400 w-6 h-6" /> Timeline Blueprint: Batch 1 (First 3-4 Videos)
                    </h2>

                    <div className="relative border-l border-white/10 ml-4 md:ml-6 space-y-10 pb-4">
                        
                        {/* Phase 1 */}
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center border-[4px] border-[#07090E] shadow-[0_0_15px_rgba(99,102,241,0.5)]">
                                <span className="text-white text-xs font-bold">1</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Onboarding & Architecture</h3>
                            <p className="text-indigo-400 text-sm font-semibold mb-3 uppercase tracking-wider">Days 1–3</p>
                            <ul className="space-y-3 text-slate-300">
                                <li className="flex gap-3"><Zap className="w-5 h-5 text-indigo-400 shrink-0" /> <span><strong>Sign-off & Payment:</strong> SLA signing and processing the upfront package invoice.</span></li>
                                <li className="flex gap-3"><Zap className="w-5 h-5 text-indigo-400 shrink-0" /> <span><strong>Infrastructure Setup:</strong> You grant us access to the necessary AI generation accounts (or provide the initial credit pool).</span></li>
                                <li className="flex gap-3"><Zap className="w-5 h-5 text-indigo-400 shrink-0" /> <span><strong>Kickoff:</strong> We deploy your custom Notion Content OS and align on the final visual style, tone, and character "pinning" (start-images).</span></li>
                            </ul>
                        </div>

                        {/* Phase 2 */}
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-[#1A1D24] rounded-full flex items-center justify-center border border-white/20">
                                <span className="text-white/60 text-xs font-bold">2</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Strategy & Scripting</h3>
                            <p className="text-indigo-400/70 text-sm font-semibold mb-3 uppercase tracking-wider">Days 4–10</p>
                            <ul className="space-y-3 text-slate-300/80">
                                <li>• We execute deep niche research to map out the strategy for Batch 1.</li>
                                <li>• We draft the high-retention scripts, focusing on hooks and pacing optimized for the YouTube algorithm.</li>
                                <li className="text-white/90 italic border-l-2 border-indigo-500/50 pl-3">"You review and approve the storyboards/scripts before we generate a single frame."</li>
                            </ul>
                        </div>

                        {/* Phase 3 */}
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-[#1A1D24] rounded-full flex items-center justify-center border border-white/20">
                                <span className="text-white/60 text-xs font-bold">3</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">The AI Production Machine</h3>
                            <p className="text-indigo-400/70 text-sm font-semibold mb-3 uppercase tracking-wider">Days 11–22</p>
                            <ul className="space-y-3 text-slate-300/80">
                                <li>• <strong>Visuals:</strong> We generate the cinematic structural clips, dialing in visual continuity across all scenes.</li>
                                <li>• <strong>Audio:</strong> We map the human-like AI voiceovers and layer in custom-generated background soundtracks and SFX.</li>
                            </ul>
                        </div>

                        {/* Phase 4 */}
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-[#1A1D24] rounded-full flex items-center justify-center border border-white/20">
                                <span className="text-white/60 text-xs font-bold">4</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Post-Production & Final Polish</h3>
                            <p className="text-indigo-400/70 text-sm font-semibold mb-3 uppercase tracking-wider">Days 23–28</p>
                            <ul className="space-y-3 text-slate-300/80">
                                <li>• Seamless editing, pacing adjustments, and color grading.</li>
                                <li>• Adding dynamic text overlays and VFX to ensure the visual pacing is aggressive and engaging.</li>
                                <li className="text-white/90 italic border-l-2 border-indigo-500/50 pl-3">"We send the final cuts of Batch 1 for your QA and final approval (each video includes up to 2 rounds of revisions)."</li>
                            </ul>
                        </div>

                        {/* Phase 5 */}
                        <div className="relative pl-8 md:pl-10">
                            <div className="absolute -left-[14px] top-1 w-7 h-7 bg-[#1A1D24] rounded-full flex items-center justify-center border border-white/20">
                                <span className="text-white/60 text-xs font-bold">5</span>
                            </div>
                            <h3 className="text-xl font-bold mb-1 text-white">Distribution & Re-purposing</h3>
                            <p className="text-indigo-400/70 text-sm font-semibold mb-3 uppercase tracking-wider">Days 29+</p>
                            <ul className="space-y-3 text-slate-300/80">
                                <li>• We handle the YouTube SEO (optimizing titles, custom thumbnails, algorithm-friendly descriptions).</li>
                                <li>• We extract the best hooks from the long-form videos to create your 16:9 vertical clips (Reels/Shorts) and distribute them across platforms to maximize reach.</li>
                            </ul>
                        </div>

                    </div>
                    
                    <div className="bg-indigo-500/10 border border-indigo-500/20 p-5 rounded-xl text-indigo-200 text-sm leading-relaxed mt-6">
                        <strong className="text-indigo-400">Note:</strong> Once Batch 1 is dialed in and approved, the AI production machine accelerates significantly, and the remaining batches are produced with much faster turnaround times.
                    </div>
                </section>


                {/* Documents Hub */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* SLA Card */}
                    <a href="/assets/radha/radha_sla.pdf" target="_blank" rel="noreferrer" className="block p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-indigo-500/50 hover:bg-white/10 transition-all group">
                        <div className="flex items-start justify-between mb-4">
                            <div className="p-3 bg-indigo-500/20 text-indigo-400 rounded-xl">
                                <FileSignature className="w-6 h-6" />
                            </div>
                            <Download className="w-5 h-5 text-white/30 group-hover:text-indigo-400 transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold mb-1">Service Agreement (SLA)</h3>
                        <p className="text-slate-400 text-sm mb-4">Review the legal terms, deliverables, and updated provisions.</p>
                        <div className="text-indigo-400 text-sm font-bold flex items-center gap-2">
                            Download PDF <MoveRight className="w-4 h-4" />
                        </div>
                    </a>

                    {/* Invoice Card */}
                    <a href="/assets/radha/SSEO00622042026-RADHA.pdf" target="_blank" rel="noreferrer" className="block p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-emerald-500/50 hover:bg-white/10 transition-all group">
                        <div className="flex items-start justify-between mb-4">
                            <div className="p-3 bg-emerald-500/20 text-emerald-400 rounded-xl">
                                <FileText className="w-6 h-6" />
                            </div>
                            <Download className="w-5 h-5 text-white/30 group-hover:text-emerald-400 transition-colors" />
                        </div>
                        <h3 className="text-xl font-bold mb-1">Upfront Invoice</h3>
                        <p className="text-slate-400 text-sm mb-4">Invoice #SSEO006 for the 12-Video Starter DOMINATION package.</p>
                        <div className="text-emerald-400 text-sm font-bold flex items-center gap-2">
                            Download PDF <MoveRight className="w-4 h-4" />
                        </div>
                    </a>
                </section>


                {/* Payment & Next Steps */}
                <section>
                    <div className="bg-gradient-to-br from-slate-900 to-[#0F172A] border border-white/10 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                        
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-[80px] rounded-full pointer-events-none" />

                        <div className="flex items-center gap-3 mb-8 border-b border-white/10 pb-6">
                            <ShieldCheck className="w-8 h-8 text-emerald-400" />
                            <h2 className="text-3xl font-black">Payment Portal & Next Steps</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-10">
                            {/* Option A: Razorpay */}
                            <div className="space-y-4">
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-indigo-400" /> Pay via Razorpay
                                </h3>
                                <p className="text-slate-400 text-sm mb-6">Split payments available for international card processing limits.</p>
                                
                                <div className="space-y-3">
                                    <a href="https://rzp.io/rzp/hvCkPPe" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl hover:bg-indigo-500/20 hover:border-indigo-400 transition-colors">
                                        <span className="font-bold text-white">Payment Part 1 of 2</span>
                                        <span className="text-indigo-400 text-sm font-semibold flex items-center gap-1">Pay Now <MoveRight className="w-4 h-4" /></span>
                                    </a>
                                    <a href="https://rzp.io/rzp/5tNJwjv" target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl hover:bg-indigo-500/20 hover:border-indigo-400 transition-colors">
                                        <span className="font-bold text-white">Payment Part 2 of 2</span>
                                        <span className="text-indigo-400 text-sm font-semibold flex items-center gap-1">Pay Now <MoveRight className="w-4 h-4" /></span>
                                    </a>
                                </div>
                            </div>

                            {/* Option B: SWIFT */}
                            <div className="space-y-4 relative">
                                <div className="hidden md:block absolute -left-5 top-0 bottom-0 w-px bg-white/5" />
                                
                                <h3 className="text-xl font-bold flex items-center gap-2">
                                    <Layers className="w-5 h-5 text-amber-400" /> Pay via SWIFT Wire
                                </h3>
                                <div className="bg-amber-500/10 border border-amber-500/20 text-amber-100/90 text-sm p-5 rounded-xl leading-relaxed space-y-4 mt-2">
                                    <p>
                                        <strong className="text-amber-400 block mb-1">1. Set to "OUR" (Sender Pays)</strong>
                                        Please thoroughly ensure the transfer is set to the <strong>"OUR" charge code</strong>. In other methods, intermediary bank deductions are highly unpredictable and result in incorrect principal amounts hitting our account.
                                    </p>
                                    <p>
                                        <strong className="text-amber-400 block mb-1">2. Process directly in INR</strong>
                                        Your payment <strong>must be processed in INR</strong>. If transferred in USD, the forex conversion rates fluctuate wildly from bank to bank, leading to severe discrepancies and poor exchange values. 
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 pt-8 border-t border-white/10 text-center">
                            <p className="text-white/60 mb-2">Let me know if you have any questions on the SLA or timeline.</p>
                            <p className="text-lg font-bold text-white">We can get the infrastructure set up today!</p>
                        </div>
                    </div>
                </section>

            </main>
        </div>
    );
}
